/**
 * Panel picker modal (§7h).
 *
 * Opens from the "+ New roundtable" button in ProjectConversationsTab
 * (and from the Roundtable page's empty state on direct navigation).
 * Lets the user select 2–3 personas in the order they should speak,
 * then hands off to the Roundtable page to compose the first message.
 *
 * Design decisions:
 *   - Multi-select with numbered order indicators so users know that
 *     clicking Bezos first means Bezos speaks first. PRD §4.3.3
 *     resolved this as fixed user-selected order; making it visible
 *     here prevents surprise.
 *   - 2 minimum / 3 maximum enforced client-side to match the backend
 *     parseRoundtableCreateBody validation from 7d.
 *   - No free-form message input in this modal — per the scoping
 *     decision, the first message is composed on the Roundtable page
 *     itself (consistent with how single chats work: pick persona,
 *     land on chat page, type message).
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal.jsx";
import Avatar from "./Avatar.jsx";
import LoadingSkeleton from "./LoadingSkeleton.jsx";
import { useStore } from "../store/useStore.js";

const MIN_PANEL = 2;
const MAX_PANEL = 3;

export default function PanelPicker({ open, onClose, projectId = null }) {
  const navigate = useNavigate();
  const personas = useStore((s) => s.personas);
  const loading = useStore((s) => s.personasLoading);
  const loadPersonas = useStore((s) => s.loadPersonas);
  const panel = useStore((s) => s.roundtablePanel);
  const togglePanellist = useStore((s) => s.togglePanellist);
  const clearPanel = useStore((s) => s.clearPanel);
  const clearActiveRoundtable = useStore((s) => s.clearActiveRoundtable);

  // Reset the panel selection each time the modal opens so a previous
  // selection doesn't leak in.
  useEffect(() => {
    if (open) {
      loadPersonas();
      clearPanel();
    }
  }, [open, loadPersonas, clearPanel]);

  const handleClose = () => {
    clearPanel();
    onClose?.();
  };

  const handleStart = () => {
    if (panel.length < MIN_PANEL) return;
    // Clear any stale activeRoundtable state from a previous turn so
    // the Roundtable page's mount effect sees a clean slate. The
    // selected panel stays in the store (separate slice) and is read
    // by the page when the user sends the first message.
    clearActiveRoundtable();
    onClose?.();
    const dest = projectId
      ? `/projects/${projectId}/roundtable`
      : "/coaches/roundtable";
    navigate(dest);
  };

  const selectionOrder = (personaId) => {
    const index = panel.indexOf(personaId);
    return index >= 0 ? index + 1 : null;
  };

  const isFull = panel.length >= MAX_PANEL;
  const canStart = panel.length >= MIN_PANEL;

  return (
    <Modal open={open} onClose={handleClose} title="Start a roundtable" wide>
      <p className="panel-picker-hint">
        Pick {MIN_PANEL}–{MAX_PANEL} personas. They&apos;ll respond in the
        order you select them.
      </p>

      <div className="panel-picker-grid">
        {loading && personas.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <LoadingSkeleton key={i} variant="card" />
          ))
        ) : (
          personas.map((p) => {
            const order = selectionOrder(p.id);
            const selected = order !== null;
            const disabled = !selected && isFull;
            return (
              <button
                key={p.id}
                type="button"
                className={`panel-picker-card ${
                  selected ? "panel-picker-card-selected" : ""
                } ${disabled ? "panel-picker-card-disabled" : ""}`}
                onClick={() => togglePanellist(p.id)}
                disabled={disabled}
                aria-pressed={selected}
              >
                {selected && (
                  <span className="panel-picker-order" aria-hidden="true">
                    {order}
                  </span>
                )}
                <Avatar persona={p} />
                <div className="panel-picker-card-body">
                  <div className="persona-name">{p.name}</div>
                  <span className="persona-category">{p.category}</span>
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="form-actions">
        <span className="panel-picker-count">
          {panel.length} / {MAX_PANEL} selected
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleStart}
          disabled={!canStart}
        >
          Start roundtable
        </button>
      </div>
    </Modal>
  );
}
